import { useTheme } from "../../context/useThemeContext";
import { ProfilePicture } from "./components/ProfilePicture";
import ToggleIconButton from "../../components/buttons/toggleIconButton";
import { IoClose } from "react-icons/io5";
import useAccountManagementStore, {
    AccountManagementStoreType,
} from "../../stores/accountManagementStore";
import SignOutView from "./views/signOutView";
import SignInView from "./views/SignInView";
import RegisterView from "./views/RegisterView";
import ForgotPasswordView from "./views/ForgotPassword";
import VerifyAccount from "./views/VerifyAccount";
import UpdateInformationView from "./views/UpdateInformation";
import DeleteAccountConfirm from "./views/DeleteAccountConfirm";
import CustomMessage from "./views/CustomMessage";
import ResetPassword from "./views/ResetPassword";
import LoginBoxContainer from "./layout/loginBoxContainer";

import { useEffect } from "react";

export default function AccountManagement() {
    return (
        <>
            <LoginBoxContainer>
                <Content />
            </LoginBoxContainer>
            <ManageLocation />
        </>
    );
}

function ManageLocation() {
    const { setLoginState } = useAccountManagementStore((state) => state);

    useEffect(() => {
        const handleLocationChange = () => {
            if (window.location.pathname === "/password-reset") {
                setLoginState("loggedOff_ResetPassword");
            }
        };

        // Run on initial load
        handleLocationChange();

        // Listen for browser navigation (back/forward)
        window.addEventListener("popstate", handleLocationChange);

        return () => {
            window.removeEventListener("popstate", handleLocationChange);
        };
    }, []);

    return <></>;
}

function Content() {
    const { theme } = useTheme();
    const { loginState, setLoginState, userData } = useAccountManagementStore();

    return (
        <>
            <div>
                <ProfilePicture />
                <ToggleIconButton
                    alt="Close Account Management"
                    icon={<IoClose size={23} />}
                    size={{ x: 25, y: 25 }}
                    className="absolute right-[10px] ml-[10px] mt-[10px]"
                    hoverBGInactive={theme.secondaryHeaderColor}
                    inactiveIconColor={theme.fontColor[300]}
                    hoverIconInactive={theme.fontColor[200]}
                    onClick={() => {
                        setLoginState(undefined);
                    }}
                />
                {getFormContent(userData, loginState)}
            </div>
        </>
    );
}

/**
 * Determines which login form to render based on the user's login state
 * @param {AccountManagementStoreType["userData"]} userData - The user's data from the store
 * @param {AccountManagementStoreType["loginState"]} loginState - The current login state
 * @returns {JSX.Element} The rendered form
 */
function getFormContent(
    userData: AccountManagementStoreType["userData"],
    loginState: AccountManagementStoreType["loginState"],
) {
    // User is NOT signed in
    if (userData?.id === undefined) {
        switch (loginState) {
            case "register":
                return <RegisterView />;
            case "forgotPassword":
                return <ForgotPasswordView />;
            case "checkEmail_CreateAccount":
                return (
                    <CustomMessage
                        pageHeaderText="Verify Account"
                        pageText="Check your email to verify your account"
                        // subText="If no email was received, check your SPAM folder"
                    />
                );
            case "checkEmail_ResetPassword":
                return (
                    <CustomMessage
                        pageHeaderText="Reset Password"
                        pageText="Successfully reset password"
                    />
                );
            case "checkEmail_ForgotPassword":
                return (
                    <CustomMessage
                        pageHeaderText="Forgot Password"
                        pageText="Check your email to update your password"
                    />
                );
            case "loggedOff_ResetPassword":
                return <ResetPassword />;
            // Default
            case "default":
            default:
                return <SignInView />;
        }
    }
    // User IS signed in
    else {
        switch (loginState) {
            // case "confirmAccount":
            //     return <VerifyAccount />;
            case "updateAccount":
                return <UpdateInformationView />;
            case "deleteAccountConfirm":
                return <DeleteAccountConfirm />;
            case "checkEmail_UpdateEmail":
                return (
                    <CustomMessage
                        pageHeaderText="Update Email"
                        pageText="Check both email addresses to update your account"
                        // pageText="Check new email address to update your account"
                    />
                );
            // Default
            case "default":
            default:
                return <SignOutView />;
        }
    }
}
